/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { getHotelFocusOnValue } from 'modules/transactions/functions';
import { TAGS } from 'modules/transactions/constants';
import { signInActions } from '../sign-in/slice';
/**
 * Initial states of scenario function are defined here
 */
export const initialState = {
  //
  loading: false,
  scenarios: [],
  scenarioById: [],
  isSessionTimedOut: false,
  visualFilters: {},
  chartBreakdown: null,
  breakdown: [],
  hotelName: null,
  allHotelList: [],
  visuals: [],
};
/**
 * All actions related to scenario feature are defined here
 */
export const scenarioSlice = createSlice({
  name: 'feature/scenario',
  initialState,
  reducers: {
    getScenarios(state) {
      state.loading = true;
    },
    getScenariosSucceeded(state, action) {
      state.loading = false;
      state.scenarios = action?.payload.results;
    },
    getScenariosFailed(state) {
      state.loading = false;
    },
    createScenarios(state) {
      state.loading = true;
    },
    createScenariosSucceeded(state, action) {
      state.loading = false;
      state.scenarios = [...state.scenarios, action?.payload];
    },
    createScenariosFailed(state) {
      state.loading = false;
    },
    updateScenario(state) {
      state.loading = true;
    },
    updateScenarioSucceeded(state, action) {
      state.loading = false;
      state.scenarios = state?.scenarios?.map((object) =>
        object?.id === action?.payload?.id ? { ...object, ...action.payload } : object
      );
    },
    updateScenarioFailed(state) {
      state.loading = false;
    },
    deleteScenario(state) {
      state.loading = true;
    },
    deleteScenarioSucceeded(state, action) {
      state.loading = false;
      state.scenarios = state?.scenarios?.filter(
        (object) => object?.id !== action?.payload?.data?.id
      );
    },
    deleteScenarioFailed(state) {
      state.loading = false;
    },
    getScenarioById(state) {
      state.loading = true;
    },
    getScenarioByIdSucceeded(state, action) {
      state.loading = false;
      state.scenarioById = action?.payload;
    },
    getScenarioByIdFailed(state) {
      state.loading = false;
    },
    setSessionTimeOut(state, action) {
      state.isSessionTimedOut = action?.payload;
    },
    getVisuals(state) {
      state.loading = true;
    },
    getVisualsSucceeded(state, action) {
      state.loading = false;
      state.visuals = action?.payload?.results;
    },
    getVisualsFailed(state) {
      state.loading = false;
    },
    getVisualFilters(state) {
      state.loading = true;
    },
    getVisualFiltersSucceeded(state, action) {
      state.loading = false;
      state.visualFilters = action?.payload;
      state.chartBreakdown = action?.payload?.results?.find(
        (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
      )?.id;
    },
    getVisualFiltersFailed(state) {
      state.loading = false;
    },
    getAllHotelList(state) {
      state.loading = true;
    },
    getAllHotelListSucceeded(state, action) {
      state.loading = false;
      const hotel = getHotelFocusOnValue(state.isDemoUser, action?.payload);
      state.allHotelList = hotel?.hotelDropdownList;
      state.hotelName = hotel?.hotelDropdownList[0];
    },
    getAllHotelListFailed(state) {
      state.loading = false;
    },
    getBreakdown(state) {
      state.loading = true;
    },
    getBreakdownSucceeded(state, action) {
      state.loading = false;
      state.breakdown = action?.payload?.results;
    },
    getBreakdownFailed(state) {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signInActions.signIn, (state) => ({
      ...state,
      ...initialState,
    }));
  },
});
//
export const { actions: scenarioActions } = scenarioSlice;
